import * as React from "react";
import { css } from "@emotion/core";
import * as mapboxgl from "mapbox-gl";
import { ResizeSensor } from "@blueprintjs/core";

import MapOverlay from "./MapOverlay";

import LayersForSurvey from "./layers/LayersForSurvey";
import Annotations from "./annotations/Annotations";

import Survey from "../../../models/Survey";
import Layer from "../../../models/Layer";
import Annotation from "../../../models/Annotation";
import Asset from "../../../models/Asset";

import ApiAnnotations from "../../../api/annotations";

import {
  useMapState,
  useMapActions,
  useDrawingState,
  useAnnotationActions,
  useSinkState,
  useDrawingActions,
} from "context/";
(mapboxgl as any).accessToken = process.env.REACT_APP_MAPBOX_KEY;
import { isValidCoordinates } from "utils/mapping/coords";
import { useParams } from "react-router-dom";

const buildGeoJsonPointFromLngLat = (lngLat) => ({
  type: "Point",
  coordinates: [lngLat.lng, lngLat.lat],
});

const flyToGeneralsView = (surveys, map) => {
  flyToBoundingBox(
    surveys
      .filter(
        (s) =>
          s.coordinates &&
          isValidCoordinates(s.coordinates.latitude, s.coordinates.longitude)
      )
      .map((s) => [s.coordinates.longitude, s.coordinates.latitude]),
    map
  );
};

const flyToBoundingBox = (coordsSet: any[], map): void => {
  if (coordsSet.length === 0) return;

  const box = coordsSet.reduce((bounds, coords) => {
    return bounds.extend(coords);
  }, new mapboxgl.LngLatBounds(coordsSet[0], coordsSet[0]));

  map.fitBounds(box, { animate: false, padding: 200 });
};

const Map = ({ layers, surveys, annotations }) => {
  const params = useParams();
  const selectedSurveyId = (params.id && Number(params.id)) || null;

  const { select } = useAnnotationActions();
  const { mode, activeDrawingLayer } = useDrawingState();
  const { setActiveDrawingLayerImmediate } = useDrawingActions();
  const { loaded, styles, selectedStyle, zoom } = useMapState();
  const { setViewport, setMap, setLoaded, setZoom } = useMapActions();
  const { sink } = useSinkState();
  const [switchTo, setSwitchTo] = React.useState(null);

  const coordinatesSubscription = React.useRef(null);
  const latestCoordsRef = React.useRef(null);
  const coordsRafRef = React.useRef(null);

  const selectedSurvey = surveys.find(
    (survey) => survey.id === selectedSurveyId
  );

  const id = selectedSurvey?.id;
  const zoom_start = selectedSurvey?.zoom_start;

  const surveyLatitude = selectedSurvey?.location?.coordinates[1];
  const surveyLongitude = selectedSurvey?.location?.coordinates[0];

  const site =
    selectedSurvey &&
    sink.sites.find((site) => site.id === selectedSurvey.site_id);

  const siteLatitude = site?.location[1];
  const siteLongitude = site?.location[0];

  const location = sink && sink.organization && sink.organization.location;

  const organizationLatitude = (location && location.coordinates[1]) || 49;
  const organizationLongitude = (location && location.coordinates[0]) || -125;

  const center: mapboxgl.LngLatLike = React.useMemo(
    () =>
      surveyLatitude && surveyLongitude
        ? [surveyLongitude, surveyLatitude]
        : siteLatitude && siteLongitude
        ? [siteLongitude, siteLatitude]
        : organizationLatitude && organizationLongitude
        ? [organizationLongitude, organizationLatitude]
        : [-125, 49],
    [
      surveyLatitude,
      surveyLongitude,
      siteLatitude,
      siteLongitude,
      organizationLatitude,
      organizationLongitude,
    ]
  );

  const mapRef = React.useRef(null);

  React.useEffect(() => {
    if (mapRef.current) {
      return;
    }

    mapRef.current = new mapboxgl.Map({
      container: "map",
      style: styles[selectedStyle],
      center: isValidCoordinates(center[1], center[0])
        ? center
        : [organizationLongitude, organizationLatitude],
      zoom: zoom_start || 5,
    });

    Layer.connectMapbox(mapRef.current);
    Asset.connectMapbox(mapRef.current);

    mapRef.current.on("load", () => {
      setLoaded(true);

      if (coordinatesSubscription.current) {
        const { lng, lat } = mapRef.current.getCenter();
        coordinatesSubscription.current({
          longitude: lng,
          latitude: lat,
        });
      }
    });

    mapRef.current.on("moveend", () => setViewport(mapRef.current.getBounds()));
    mapRef.current.on("zoomend", () => {
      setZoom(mapRef.current.getZoom().toFixed(1));
      setViewport(mapRef.current.getBounds());
    });
    mapRef.current.on("mousemove", (event) => {
      latestCoordsRef.current = {
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
      };
      if (coordsRafRef.current !== null) return;
      coordsRafRef.current = requestAnimationFrame(() => {
        coordsRafRef.current = null;
        if (coordinatesSubscription.current && latestCoordsRef.current) {
          coordinatesSubscription.current(latestCoordsRef.current);
        }
      });
    });

    mapRef.current.on("style.load", (event) => {
      setSwitchTo(null);
    });

    mapRef.current.addControl(
      new mapboxgl.ScaleControl({
        unit: "metric",
      })
    );

    mapRef.current.addControl(
      new mapboxgl.ScaleControl({
        unit: "imperial",
      })
    );

    if (!selectedSurvey) {
      // flyToGeneralsView(surveys, mapRef.current);
    }

    setMap(mapRef.current);
    setZoom(mapRef.current.getZoom().toFixed(1));

    return () => {
      if (coordsRafRef.current !== null) {
        cancelAnimationFrame(coordsRafRef.current);
        coordsRafRef.current = null;
      }
    };
  }, [center, organizationLatitude, organizationLongitude]);

  React.useEffect(() => {
    if (mapRef.current) {
      const click = (event) => {
        if (mode === "marker") {
          const geometry = buildGeoJsonPointFromLngLat(event.lngLat);
          const survey = Survey.find(selectedSurveyId);
          ApiAnnotations.createAnnotation(
            activeDrawingLayer && activeDrawingLayer.id,
            { geometry },
            survey.id
          ).then((annotation) => {
            setActiveDrawingLayerImmediate(annotation.layer);
            select(annotation.id);
          });
        }
      };
      mapRef.current.on("click", click);
      return () => mapRef.current.off("click", click);
    }
  }, [activeDrawingLayer, mode]);

  React.useEffect(() => {
    if (id) {
      mapRef.current.jumpTo({
        center: isValidCoordinates(center[1], center[0])
          ? center
          : [organizationLongitude, organizationLatitude],
        zoom: zoom_start || 5,
      });
    }
  }, [id, center, zoom_start, organizationLatitude, organizationLongitude]);

  React.useEffect(() => {
    if (!id) {
      // flyToGeneralsView(surveys, mapRef.current);
    }
  }, [id, flyToGeneralsView, surveys]);

  React.useEffect(() => {
    if (mapRef.current) {
      setSwitchTo(selectedStyle);
    }
  }, [selectedStyle]);

  React.useEffect(() => {
    if (mapRef.current && switchTo) {
      mapRef.current.setStyle(styles[selectedStyle]);
    }
  }, [switchTo, selectedStyle]);

  const subscribeToCoordinates = React.useCallback((fn) => {
    coordinatesSubscription.current = fn;
    return { destroy: () => (coordinatesSubscription.current = null) };
  }, []);

  const annotationsForSurvey = Annotation.forSurvey(selectedSurveyId);

  return (
    <ResizeSensor onResize={() => mapRef.current.resize()}>
      <main
        id="map"
        css={css`
          position: absolute;
          width: 100%;
          height: 100%;
        `}
      >
        {loaded && (
          <>
            {switchTo === null && (
              <>
                <LayersForSurvey
                  map={mapRef.current}
                  surveyId={selectedSurveyId}
                  layers={layers}
                />
                <Annotations annotations={annotations} map={mapRef.current} />
              </>
            )}
            <MapOverlay
              zoom={zoom}
              subscribeToCoordinates={subscribeToCoordinates}
            />
          </>
        )}
      </main>
    </ResizeSensor>
  );
};

export default Map;
